import React from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { TIERS } from "../lib/tiers";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { EText, EList, EImg } from "./cms/Editable";

export default function PricingCards({ onSelect }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { editMode } = useSite();

  const handleClick = (tier) => {
    if (editMode) return;
    if (onSelect) return onSelect(tier);
    if (tier === "silver") {
      navigate(user ? "/dashboard" : "/signup");
    } else {
      navigate(`/packages?tier=${tier}&checkout=true`);
    }
  };

  return (
    <div className="grid gap-6 lg:gap-8 md:grid-cols-3 max-w-6xl mx-auto">
      {TIERS.map((t, idx) => (
        <motion.div
          key={t.id}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: idx * 0.1 }}
          data-testid={`pricing-card-${t.id}`}
          className={`card-surface relative p-8 flex flex-col ${t.id === "gold" ? "md:-mt-4 md:mb-4 shadow-xl !border-2 !border-[#C9A24B] ring-4 ring-[#E8C872]/30" : ""}`}
        >
          {t.launch && (
            <span className="pill absolute -top-3 left-1/2 -translate-x-1/2 text-white" style={{ background: "var(--tan)" }}>
              <EText k="tier.launch_label">Launch Pricing</EText>
            </span>
          )}
          {t.image && (
            <EImg k={`tier.${t.id}.image`} src={t.image} alt={t.name}
              className="w-full aspect-[16/10] object-cover rounded-xl" wrapperClassName="relative -mt-2 mb-5" />
          )}
          <div className="text-center">
            <EText k={`tier.${t.id}.name`} as="h3" className="font-display text-3xl font-semibold text-brand-ink block">{t.name}</EText>
            <div className="mt-3 flex items-end justify-center gap-2">
              <EText k={`tier.${t.id}.price`} className="font-display text-5xl font-semibold text-brand-ink">{t.price}</EText>
              {t.price !== "Free" && <EText k={`tier.${t.id}.priceSub`} className="text-brand-muted mb-1.5">{t.priceSub}</EText>}
            </div>
            {t.price === "Free" && <EText k={`tier.${t.id}.priceSub`} as="p" className="text-brand-muted text-sm mt-1 block">{t.priceSub}</EText>}
          </div>

          <EList k={`tier.${t.id}.features`} items={t.features} className="mt-7 space-y-3 flex-1" render={(f, i) => (
            <li key={i} className="flex items-start gap-3 text-brand-inksoft text-sm">
              <Check size={18} className="text-brand-sage shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          )} />

          {t.includes && (
            <EText k={`tier.${t.id}.includes`} as="p" className="mt-4 text-xs italic text-brand-muted text-center block">{t.includes}</EText>
          )}

          <button
            onClick={() => handleClick(t.id)}
            data-testid={`buy-${t.id}-button`}
            className={`mt-6 w-full ${t.id === "gold" ? "btn-tier-gold" : t.id === "diamond" ? "btn-tier-silver" : "btn-primary"}`}
          >
            <EText k={`tier.${t.id}.button`}>{t.button}</EText>
          </button>
        </motion.div>
      ))}
    </div>
  );
}
